import { type Analytics, getAnalytics } from 'firebase/analytics';
import { type FirebaseApp, initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import {
  type Auth,
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  initializeRecaptchaConfig,
  setPersistence,
} from 'firebase/auth';
import { connectFirestoreEmulator, type Firestore, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, type Functions, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, type FirebaseStorage, getStorage } from 'firebase/storage';

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

export const firebaseConfig: FirebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || import.meta.env.VITE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || import.meta.env.VITE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || import.meta.env.VITE_PROJECT_ID,
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || import.meta.env.VITE_STORAGE_BUCKET,
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || import.meta.env.VITE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || import.meta.env.VITE_APP_ID,
  measurementId:
    import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || import.meta.env.VITE_MEASUREMENT_ID,
};

const app: FirebaseApp = initializeApp(firebaseConfig);

// Initialize App Check
// Make sure to set the VITE_RECAPTCHA_SITE_KEY in your .env file
// You can get this key from the Firebase console -> App Check -> Apps -> your web app

// Set debug token for development/testing environments
// This allows tests and local development to bypass App Check verification
if (import.meta.env.VITE_APPCHECK_DEBUG_TOKEN) {
  // @ts-expect-error - This is a special Firebase App Check property
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
}

const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

// Declare appCheck export
export let appCheck: import('firebase/app-check').AppCheck | undefined;

if (recaptchaSiteKey) {
  // Initialize App Check
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

// Handle unhandled promise rejections from App Check token refresh
// These timeouts are non-critical since App Check is in Monitor mode
window.addEventListener('unhandledrejection', (event) => {
  // Check if the error is from App Check timeout or Recaptcha collision
  const reasonStr = event.reason?.toString() || '';
  if (
    event.reason &&
    (event.reason.message?.includes('Timeout') ||
      event.reason.name === 'Timeout' ||
      reasonStr.includes('Timeout') ||
      reasonStr.includes('n.render is not a function'))
  ) {
    // Suppress App Check timeout / Recaptcha collision errors in console as they don't affect functionality
    if (!import.meta.env.DEV) {
      event.preventDefault();
    } else {
      event.preventDefault();
    }
  }
});

const auth: Auth = getAuth(app);

const analytics: Analytics = getAnalytics(app);
const firestore: Firestore = getFirestore(app);
const functions: Functions = getFunctions(
  app,
  import.meta.env.VITE_GCP_REGION || 'australia-southeast1'
);
const storage: FirebaseStorage = getStorage(app);

if (
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
  import.meta.env.VITE_USE_EMULATORS !== 'false'
) {
  try {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099');
    connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    connectStorageEmulator(storage, '127.0.0.1', 9199);
  } catch {
    // Emulator connection may fail if already connected or emulators not running
  }
}

// Must initialize reCAPTCHA Enterprise config for SMS Defense in ENFORCE mode
// This must happen AFTER connectAuthEmulator
try {
  const recaptchaPromise = initializeRecaptchaConfig(auth);
  if (recaptchaPromise?.catch) {
    recaptchaPromise.catch(() => {
      // Silent fail, handled internally by Firebase
    });
  }
} catch (_error) {
  // Silent fail
}
setPersistence(auth, browserLocalPersistence);

export { analytics, app, auth, firestore, functions, storage };
